import type { LucideIcon } from 'lucide-react';
import {
  Archive,
  ArchiveRestore,
  Square,
  XCircle,
  Zap,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';

type BulkProcessButton = {
  show: boolean;
  enabled: boolean;
  disabledTitleKey?: string;
};

type Props = {
  selectedCount: number;
  isArchiveView: boolean;
  writeBlocked: boolean;
  bulkActionRunning: boolean;
  selectedStopCount: number;
  bulkProcessButton: BulkProcessButton;
  processQueueActiveCount: number;
  onBulkUnarchive: () => void;
  onBulkStopProcessing: () => void;
  onBulkProcess: () => void;
  onBulkArchive: () => void;
  onBulkReject: () => void;
  onClearSelection: () => void;
  guardWrite: () => boolean;
  className?: string;
};

function BulkActionButton({
  icon: Icon,
  label,
  suffix,
  onClick,
  disabled,
  title,
  className,
}: {
  icon: LucideIcon;
  label: string;
  suffix?: string;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  className?: string;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className={cn('h-8 gap-1.5 max-sm:px-2', className)}
      onClick={onClick}
      disabled={disabled}
      title={title ?? label}
      aria-label={label}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" />
      <span className="hidden sm:inline">{label}</span>
      {suffix ? <span className="hidden sm:inline text-xs text-muted-foreground tabular-nums">{suffix}</span> : null}
    </Button>
  );
}

/**
 * Bulk actions when one or more application rows are selected.
 * Mobile: count + clear on first row; icon-only action buttons wrap on second row.
 */
export function ApplicationsBulkActionBar({
  selectedCount,
  isArchiveView,
  writeBlocked,
  bulkActionRunning,
  selectedStopCount,
  bulkProcessButton,
  processQueueActiveCount,
  onBulkUnarchive,
  onBulkStopProcessing,
  onBulkProcess,
  onBulkArchive,
  onBulkReject,
  onClearSelection,
  guardWrite,
  className,
}: Props) {
  const { t } = useLanguage();

  return (
    <div
      className={cn(
        'shrink-0 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2.5 text-sm sm:px-4',
        className,
      )}
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
        <div className="flex min-w-0 items-center justify-between gap-2 sm:shrink-0">
          <span className="font-medium truncate">
            {t('applications.bulkSelectedCount', { count: selectedCount })}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 shrink-0 sm:hidden"
            onClick={onClearSelection}
            disabled={bulkActionRunning}
          >
            {t('applications.bulkClear')}
          </Button>
        </div>

        <div className="flex min-w-0 flex-wrap items-center gap-1.5 sm:ml-auto sm:justify-end sm:gap-2">
          {isArchiveView ? (
            <BulkActionButton
              icon={ArchiveRestore}
              label={t('applications.bulkUnarchive')}
              onClick={onBulkUnarchive}
              disabled={bulkActionRunning || writeBlocked}
            />
          ) : (
            <>
              {selectedStopCount > 0 && (
                <BulkActionButton
                  icon={Square}
                  label={t('applications.bulkStop')}
                  suffix={`(${selectedStopCount})`}
                  onClick={onBulkStopProcessing}
                  disabled={writeBlocked}
                  title={t('applications.bulkStopTooltip')}
                  className="border-amber-600/40 text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950/30"
                />
              )}
              {bulkProcessButton.show && (
                <BulkActionButton
                  icon={Zap}
                  label={t('applications.bulkProcess')}
                  suffix={processQueueActiveCount > 0 ? `(${processQueueActiveCount})` : undefined}
                  onClick={() => {
                    if (!guardWrite()) return;
                    onBulkProcess();
                  }}
                  disabled={!bulkProcessButton.enabled || writeBlocked}
                  title={
                    bulkProcessButton.disabledTitleKey
                      ? t(bulkProcessButton.disabledTitleKey)
                      : undefined
                  }
                  className="border-emerald-600/40 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 disabled:pointer-events-auto"
                />
              )}
              <BulkActionButton
                icon={Archive}
                label={t('applications.bulkArchive')}
                onClick={onBulkArchive}
                disabled={bulkActionRunning || writeBlocked}
              />
              <BulkActionButton
                icon={XCircle}
                label={t('applications.bulkReject')}
                onClick={() => {
                  if (!guardWrite()) return;
                  onBulkReject();
                }}
                disabled={bulkActionRunning || writeBlocked}
                className="border-destructive/40 text-destructive hover:bg-destructive/10"
              />
            </>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="hidden h-8 sm:inline-flex"
            onClick={onClearSelection}
            disabled={bulkActionRunning}
          >
            {t('applications.bulkClear')}
          </Button>
        </div>
      </div>
    </div>
  );
}
